import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { EventStateEnum, EventType } from '@app/core/enums/event.enum';
import { OpportunitySaleStateEnum } from '@app/core/enums/opportunity-sale-state.enum';
import { ISelectItem } from '@app/core/interfaces/select-item.interface';
import { DomainValuesSharedService } from '@app/core/services/domain-values.shared.service';
import { SharedLibModule } from '@app/core/shared-lib/shared-lib.module';
import { FutureEventCreationModalComponent } from '@app/features/sales/components/future-event-creation-modal/future-event-creation-modal.component';
import dayjs from 'dayjs';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { lastValueFrom, map, Observable, of } from 'rxjs';
import { EventOutcomeFacade } from './event-outcome.facade';

@Component({
  selector: 'event-outcome-modal',
  templateUrl: `event-outcome-modal.html`,
  standalone: true,
  imports: [SharedLibModule],
  styles: [],
})
export class EventOutcomeModal implements OnInit {
  fb = inject(FormBuilder);
  dialogConfig = inject(DynamicDialogConfig);
  dialogRef = inject(DynamicDialogRef<EventOutcomeModal>);
  dialog = inject(DialogService);
  eventOutcomeFacade = inject(EventOutcomeFacade);
  domainValuesSharedService = inject(DomainValuesSharedService);
  eventId = this.dialogConfig.data.eventId;
  eventStateOptions$: Observable<ISelectItem[]> = of([]);
  disputeReasonOptions$ = this.domainValuesSharedService.getEventDisputeReason();
  EventState = EventStateEnum;
  EventType = EventType;
  isFormEnabled = true;

  formEvent = this.fb.group({
    projectId: this.fb.nonNullable.control<string | undefined>(undefined),
    state: this.fb.nonNullable.control<EventStateEnum | undefined>(undefined, [Validators.required]),
    note: this.fb.nonNullable.control<string | undefined>(undefined, [Validators.required]),
    oldNote: this.fb.nonNullable.control<string | undefined>(undefined),
    dispute: this.fb.group({
      reason: this.fb.nonNullable.control<number | undefined>(undefined),
      note: this.fb.nonNullable.control<string | undefined>(undefined),
    }),
    reschedule: this.fb.group({
      reason: this.fb.nonNullable.control<number | undefined>(undefined),
      newDate: this.fb.nonNullable.control<string | undefined>(undefined),
      newStart: this.fb.nonNullable.control<string | undefined>(undefined),
      bypassReminder: this.fb.nonNullable.control<boolean>(false),
    }),
    type: this.fb.nonNullable.control<EventType | undefined>(undefined),
    propertyAddress: this.fb.nonNullable.control<string | undefined>(undefined),
    contactAddress: this.fb.nonNullable.control<string | undefined>(undefined),
    openHouseParentEvent: this.fb.nonNullable.control<string | undefined>(undefined),
    opportunityState: this.fb.nonNullable.control<OpportunitySaleStateEnum | undefined>(undefined),
    rescheduleCount: this.fb.nonNullable.control<number | undefined>(undefined),
    userId: this.fb.nonNullable.control<string | undefined>(undefined),
  });

  slotList: string[] = [];

  ngOnInit(): void {
    this.eventOutcomeFacade.getEventOutcome(this.eventId).subscribe((eventOutcome) => {
      this.formEvent.patchValue(eventOutcome);
      this.initEventOptions();
    });

    if (this.formEvent.value.type === EventType.PERSONALE) {
      this.formEvent.get('state')?.disable();
    }

    this.isFormEnabled = ![
      EventStateEnum.ANNULLATO,
      EventStateEnum.CONTESTATO,
      EventStateEnum.ESEGUITO,
      EventStateEnum.RIPROGRAMMATO,
    ].includes(this.formEvent.value.state!);

    // Gestione validazione condizionale per i campi reschedule
    this.formEvent.get('state')?.valueChanges.subscribe((state) => {
      const rescheduleGroup = this.formEvent.get('reschedule');
      const newDateControl = rescheduleGroup?.get('newDate');
      const newStartControl = rescheduleGroup?.get('newStart');

      if (state === EventStateEnum.RIPROGRAMMATO) {
        newDateControl?.setValidators([Validators.required]);
        newStartControl?.setValidators([Validators.required]);
      } else {
        newDateControl?.clearValidators();
        newStartControl?.clearValidators();
      }
      
      newDateControl?.updateValueAndValidity();
      newStartControl?.updateValueAndValidity();
    });
  }

  initEventOptions() {
    const event = this.formEvent.value;
    let eventStateList = [EventStateEnum.ESEGUITO, EventStateEnum.RIPROGRAMMATO, EventStateEnum.ANNULLATO];
    if (event.projectId) {
      if (event.type == EventType.SOPRALLUOGO /*  && sourceType == SourceTypeDomainValueType.CAPTIVE */) {
        eventStateList = [EventStateEnum.ESEGUITO, EventStateEnum.RIPROGRAMMATO, EventStateEnum.ANNULLATO];
      }

      if (event.opportunityState === OpportunitySaleStateEnum.VENDUTO) {
        eventStateList = [EventStateEnum.ESEGUITO, EventStateEnum.ANNULLATO];
      }

      if ((event.type === EventType.VISITA_IMMOBILE && event.openHouseParentEvent) || event.type === EventType.OPENHOUSE) {
        eventStateList = [EventStateEnum.ESEGUITO, EventStateEnum.ANNULLATO];
      }
      if (event.rescheduleCount! >= 6) {
        eventStateList = [EventStateEnum.ESEGUITO, EventStateEnum.ANNULLATO];
      }
    }

    this.eventStateOptions$ = this.domainValuesSharedService
      .getEventState()
      .pipe(map((eventState) => eventState.filter((state) => eventStateList.includes(state.value))));
  }

  close() {
    this.dialogRef.close();
  }

  async save() {
    if (this.formEvent.valid) {
      const formValue = this.formEvent.value;

      if (!formValue.note || !formValue.state) return;

      if (formValue.state === EventStateEnum.CONTESTATO) {
        if (formValue.dispute?.reason && formValue.dispute?.note) {
          await lastValueFrom(this.eventOutcomeFacade.disputeEvent(this.eventId, formValue.dispute.reason, formValue.dispute.note));
        }
      } else if (formValue.state === EventStateEnum.RIPROGRAMMATO) {
        if (formValue.reschedule?.bypassReminder != null && formValue.reschedule.newDate && formValue.reschedule.newStart) {
          const selectedDate = dayjs(formValue.reschedule.newDate);
          const [hours, minutes] = formValue.reschedule.newStart.split(':').map(Number);
          const newDateTime = selectedDate.hour(hours).minute(minutes).second(0).millisecond(0);
          
          await lastValueFrom(
            this.eventOutcomeFacade.rescheduleEvent(this.eventId, {
              bypassReminder: formValue.reschedule.bypassReminder,
              newDate: newDateTime.toDate(),
              note: formValue.note,
            }),
          );
        }
      } else {
        // Annullato/Eseguito/Non esitato se Personale
        if (
          formValue.state == EventStateEnum.ANNULLATO &&
          formValue.type === EventType.VISITA_IMMOBILE &&
          formValue.openHouseParentEvent
        ) {
          await lastValueFrom(this.eventOutcomeFacade.removeParticipantVisitEvent(this.eventId, formValue.note));
        } else if (
          (formValue.state == EventStateEnum.ANNULLATO || formValue.state === EventStateEnum.ESEGUITO) &&
          formValue.type === EventType.OPENHOUSE
        ) {
          await lastValueFrom(this.eventOutcomeFacade.updateAllParticipantsOpenHouse(this.eventId, formValue.note, formValue.state));
        } else {
          await lastValueFrom(this.eventOutcomeFacade.updateEventStatus(this.eventId, formValue.state, formValue.note));

          if (formValue.state == EventStateEnum.ESEGUITO && formValue.type != EventType.VISITA_IMMOBILE) {
            this.dialog.open(FutureEventCreationModalComponent, {
              header: 'Creazione evento futuro',
              width: '65%',
              data: {
                projectId: formValue.projectId,
              },
            });
          }
        }
      }

      this.dialogRef.close({ success: true });
    }
  }

  checkAgentAvailability() {
    const newDate = this.formEvent.get('reschedule')?.get('newDate')?.getRawValue();
    const start = dayjs(newDate).startOf('day').toDate();
    const end = dayjs(newDate).endOf('day').toDate();
    const slot = 0.5;
    const event = this.formEvent.value;
    const userId = event.userId;
    if (userId) {
      this.eventOutcomeFacade.getAvailabilitySlot(userId, start, end, slot).subscribe((res) => {
        this.slotList = res.map((data: string) => dayjs(data).format('HH:mm'));
      });
    }
  }
}
